// 複刻文化 Fuko — 書封骨架卡片
// 用於書籍資料尚未匯入時的視覺版型
// 顯示純色書封輪廓、書脊、排版位置，但不顯示假書名、假作者

interface BookSkeletonCardProps {
  variant: 'tall' | 'wide' | 'square';
}

const variantStyles = {
  tall: {
    coverW: 'w-[140px] md:w-[160px]',
    coverH: 'h-[200px] md:h-[230px]',
    coverBg: 'bg-primary-700',
    spineBg: 'bg-primary-800',
    spineW: 'w-[10px]',
    textGap: 'gap-3',
  },
  wide: {
    coverW: 'w-[170px] md:w-[190px]',
    coverH: 'h-[190px] md:h-[210px]',
    coverBg: 'bg-secondary-800',
    spineBg: 'bg-secondary-900',
    spineW: 'w-[9px]',
    textGap: 'gap-3',
  },
  square: {
    coverW: 'w-[150px] md:w-[170px]',
    coverH: 'h-[170px] md:h-[190px]',
    coverBg: 'bg-background-200',
    spineBg: 'bg-foreground-800',
    spineW: 'w-[9px]',
    textGap: 'gap-3',
  },
};

export default function BookSkeletonCard({ variant }: BookSkeletonCardProps) {
  const s = variantStyles[variant];

  return (
    <div className="group bg-background-50 border border-background-200/70 rounded-md overflow-hidden transition-shadow duration-300 hover:shadow-[0_2px_16px_rgba(0,0,0,0.06)]">
      <div className="p-4 md:p-5">
        <div className="flex gap-4 md:gap-5">
          {/* 書封輪廓 */}
          <div className={`${s.coverW} ${s.coverH} flex-shrink-0 ${s.coverBg} rounded-sm relative overflow-hidden`}>
            {/* 書脊 */}
            <div className={`absolute left-0 top-0 bottom-0 ${s.spineW} ${s.spineBg}`}></div>
            {/* 書封表面微光 */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/[0.06] to-transparent"></div>
            {/* 封面細節線條（模擬排版位置） */}
            <div className="absolute top-[15%] left-[12%] right-[12%]">
              <div className="h-[3px] bg-white/[0.08] rounded-full mb-1.5 w-[80%]"></div>
              <div className="h-[3px] bg-white/[0.06] rounded-full mb-1.5 w-[55%]"></div>
              <div className="h-[3px] bg-white/[0.05] rounded-full w-[65%]"></div>
            </div>
            {/* 書封底部細節 */}
            <div className="absolute bottom-[12%] left-[12%] right-[12%]">
              <div className="h-[2px] bg-white/[0.06] rounded-full mb-1 w-[40%]"></div>
              <div className="h-[2px] bg-white/[0.04] rounded-full w-[30%]"></div>
            </div>
          </div>

          {/* 文字內容位置 */}
          <div className={`flex-1 min-w-0 flex flex-col ${s.textGap}`}>
            {/* 標題位置 */}
            <div className="h-4 bg-background-300/60 rounded w-[85%]"></div>
            <div className="h-3 bg-background-300/40 rounded w-[55%]"></div>
            {/* 作者/分類位置 */}
            <div className="space-y-1.5 mt-1">
              <div className="h-2.5 bg-background-300/30 rounded w-[60%]"></div>
              <div className="h-2.5 bg-background-300/30 rounded w-[45%]"></div>
            </div>
            {/* 介紹文字位置 */}
            <div className="space-y-1.5 mt-2">
              <div className="h-2 bg-background-300/25 rounded w-full"></div>
              <div className="h-2 bg-background-300/25 rounded w-[90%]"></div>
              <div className="h-2 bg-background-300/25 rounded w-[70%]"></div>
            </div>
            {/* 標籤位置 */}
            <div className="flex gap-1.5 mt-2">
              <div className="h-5 bg-background-300/30 rounded-full w-14"></div>
              <div className="h-5 bg-background-300/30 rounded-full w-20"></div>
            </div>
            {/* 按鈕位置 */}
            <div className="flex gap-3 mt-2 pt-2 border-t border-background-200/60">
              <div className="h-3 bg-background-300/30 rounded w-16"></div>
              <div className="h-3 bg-background-300/20 rounded w-12 ml-auto"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}